import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@snapchat/mw-common';

import type { AgentChatMessageSource, ChatMessage } from '../types';

export const messagesStorageKey = 'mwp-ai-agent-messages';

/**
 * Reads the chat history from session storage and validates its shape.
 * Fail-closed so callers always receive a safe-to-use array.
 *
 * @returns The stored chat history, or an empty array if no data is present,
 *   the JSON is malformed, or the shape does not match `ChatMessage[]`.
 */
export const loadMessages = (): ChatMessage[] => {
  const raw = getSessionStorageItem(messagesStorageKey);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return isValidMessages(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Writes the chat history to session storage so it survives a page refresh.
 * Removes the stored entry when the history is empty to avoid leaving stale data.
 *
 * Browser errors from the underlying write (e.g. `QuotaExceededError`) are swallowed
 * by the typed storage utils, which log a warning. The in-memory chat keeps working;
 * only the persisted copy is lost.
 *
 * @param messages - The chat history to save.
 */
export const saveMessages = (messages: ChatMessage[]): void => {
  if (messages.length === 0) {
    removeSessionStorageItem(messagesStorageKey);
  } else {
    setSessionStorageItem(messagesStorageKey, JSON.stringify(messages));
  }
};

/**
 * Removes the chat history from session storage.
 */
export const clearMessages = (): void => {
  removeSessionStorageItem(messagesStorageKey);
};

/**
 * Type predicate that checks whether an unknown value matches the `ChatMessage[]` shape.
 * Used to validate data coming from session storage before treating it as chat history.
 *
 * @param data - The value to validate.
 * @returns `true` if `data` is a well-formed `ChatMessage[]`, otherwise `false`.
 */
const isValidMessages = (data: unknown): data is ChatMessage[] =>
  Array.isArray(data) && data.every(isValidMessage);

/**
 * Type predicate that checks whether an unknown value matches the `ChatMessage` shape.
 *
 * @param value - The value to validate.
 * @returns `true` if `value` is a well-formed `ChatMessage`, otherwise `false`.
 */
const isValidMessage = (value: unknown): value is ChatMessage => {
  if (!isRecord(value)) return false;
  if (typeof value.id !== 'string') return false;
  if (value.role !== 'user' && value.role !== 'agent' && value.role !== 'error') return false;
  if (typeof value.content !== 'string') return false;

  if (value.sources !== undefined) {
    if (!Array.isArray(value.sources) || !value.sources.every(isValidSource)) return false;
  }

  return true;
};

/**
 * Type predicate that checks whether an unknown value matches the `AgentChatMessageSource` shape.
 *
 * @param value - The value to validate.
 * @returns `true` if `value` is a well-formed source, otherwise `false`.
 */
const isValidSource = (value: unknown): value is AgentChatMessageSource =>
  isRecord(value) && typeof value.title === 'string' && typeof value.url === 'string';

/**
 * Type predicate that narrows an unknown value to a plain object with string keys.
 *
 * @param value - The value to check.
 * @returns `true` if `value` is a non-null object, otherwise `false`.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
